import React, { useState, useEffect } from "react";
import { Phone, Menu, X, Ambulance } from "lucide-react";
import { CONFIG, NAV, T, telLink } from "../../data/content";
import { useLang } from "../../context/i18n";

const track = (label) => window.gtag && window.gtag("event", "cta_click", { cta: label });

export const LangToggle = ({ compact }) => {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      aria-label="Toggle language"
      className={`inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-[var(--navy)] transition-colors hover:bg-slate-100 ${compact ? "" : ""}`}
    >
      <span className={lang === "en" ? "text-[var(--navy)]" : "text-slate-400"}>EN</span>
      <span className="text-slate-300">/</span>
      <span className={lang === "hi" ? "text-[var(--navy)]" : "text-slate-400"} style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}>हिं</span>
    </button>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-sm" : "bg-white"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
        {/* Logo */}
        <a href="#home" onClick={(e) => { e.preventDefault(); go("home"); }} className="flex items-center gap-3" data-testid="logo-link">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--navy)] text-white" aria-label="Jan Seva Hospital logo placeholder">
            {/* ⬅ EDIT: replace with hospital logo <img src="/logo.png" /> */}
            <span className="font-head text-lg font-extrabold">JS</span>
          </div>
          <div className="leading-tight">
            <div className="font-head text-base font-extrabold tracking-tight text-[var(--navy)] sm:text-lg">Jan Seva Hospital</div>
            <div className="text-[11px] text-[var(--green)] font-semibold">Sikariya More, Gaya</div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`} className="text-sm font-semibold text-[var(--navy)] transition-colors hover:text-[var(--red)]">
              {t(n)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a href={telLink(CONFIG.phonePrimary)} onClick={() => track("header_phone")} className="hidden items-center gap-2 text-sm font-bold text-[var(--navy)] md:flex" data-testid="header-phone">
            <Phone size={16} /> +91 {CONFIG.phonePrimary}
          </a>
          <LangToggle />
          <a href={telLink(CONFIG.phonePrimary)} onClick={() => track("call_emergency")} data-testid="call-emergency-btn"
             className="inline-flex items-center gap-2 rounded-full bg-[var(--red)] px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-[var(--red-hover)] hover:scale-[1.03]">
            <Ambulance size={16} /> <span className="hidden sm:inline">{t(T.callEmergency)}</span>
          </a>
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden text-[var(--navy)]" aria-label="Menu" data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-slate-100 bg-white px-4 pb-4 lg:hidden" aria-label="Mobile">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`mnav-${n.id}`} className="block w-full border-b border-slate-50 py-3 text-left text-base font-semibold text-[var(--navy)]">
              {t(n)}
            </button>
          ))}
        </nav>
      )}
    </header>
  );
};

export const FloatingButtons = () => {
  const { t } = useLang();
  return (
    <div className="fixed bottom-3 left-3 right-3 z-[90] lg:hidden" data-testid="floating-buttons">
      <div className="flex items-center justify-between gap-2 rounded-full bg-white p-2 shadow-2xl ring-1 ring-slate-200">
        <a href={telLink(CONFIG.phonePrimary)} onClick={() => track("float_call")} data-testid="float-call"
           className="flex flex-1 flex-col items-center gap-0.5 rounded-full bg-[var(--navy)] py-2 text-white">
          <Phone size={18} /><span className="text-[11px] font-semibold">{t(T.call)}</span>
        </a>
        <a href={`https://wa.me/${CONFIG.whatsapp}`} target="_blank" rel="noopener noreferrer" onClick={() => track("float_whatsapp")} data-testid="float-whatsapp"
           className="flex flex-1 flex-col items-center gap-0.5 rounded-full bg-[var(--green)] py-2 text-white">
          <i className="fa-brands fa-whatsapp text-lg" aria-hidden="true"></i><span className="text-[11px] font-semibold">{t(T.whatsapp)}</span>
        </a>
        <a href={CONFIG.mapsLink} target="_blank" rel="noopener noreferrer" onClick={() => track("float_directions")} data-testid="float-directions"
           className="flex flex-1 flex-col items-center gap-0.5 rounded-full bg-white py-2 text-[var(--navy)] ring-1 ring-slate-200">
          <i className="fa-solid fa-diamond-turn-right text-lg" aria-hidden="true"></i><span className="text-[11px] font-semibold">{t(T.directions)}</span>
        </a>
      </div>
    </div>
  );
};

export { track };
